<template>
  <div class="batch-sample-filter">
    <div class="search-field-selector">
      <search-field-selector
        class="search-multiple-text"
        :default-option-index="defaultOptionIndexText"
        :selected-option="selectedTextOption"
        :text-value="textQuery"
        :options="selectOptions"
        @update:textValue="textQuery = $event"
        @updateSearchValue="setMultipleTextSearch"
        @keyup.enter.native="handleFilterSamples"
      />

      <div
        class="btn-primary btn-container"
        @click="handleFilterSamples"
      >
        <md-tooltip>Buscar</md-tooltip>

        <svg-loader src="/static/images/icon-search-white.svg" />
      </div>
    </div>

    <button-icon
      v-if="!batch.samplesCollected"
      :icon-url="iconRemoveURL"
      :disabled="disabledButtonRemove"
      @click="handleOpenRemoveSamplesModal"
    >
      {{ textButtonRemove }}
    </button-icon>

    <modal
      ref="removeSamplesModal"
      template="neutral"
      ref-name="removeSamplesModal"
      :close-button-enabled="true"
      :icon-enabled="false"
      title=""
    >
      <div class="remove-samples-modal">
        <div class="remove-samples-modal-title">
          {{ removeSamplesModalTitle }}
        </div>

        <div class="remove-samples-modal-description">
          {{ removeSamplesModalDescription }}
        </div>

        <div class="remove-samples-modal-footer">
          <md-button
            class="btn btn-nonenary"
            :disabled="isSampleBeingRemoved"
            @click="handleCloseRemoveSamplesModal"
          >
            Cancelar
          </md-button>

          <md-button
            class="btn btn-primary"
            :disabled="isSampleBeingRemoved"
            @click="handleRemoveSelectedSamples"
          >
            Continuar
          </md-button>
        </div>
      </div>
    </modal>

    <modal
      ref="removeSampleFromBatchModal"
      template="loading"
      ref-name="removeSampleFromBatchModal"
      title="Aguarde"
    >
      <p>Removendo amostra(s)...</p>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import batchService from '@/api/batch'
import notify from '@/api/notification'

import { BATCH_SOURCE_SYSTEM_WS } from '@/enums'

import SearchFieldSelector from '@/components/common/SearchFieldSelectorV2'
import ButtonIcon from '@/components/common/buttonComponent/ButtonIcon'

export default {
  name: 'BatchSampleFilter',
  components: {
    SearchFieldSelector,
    ButtonIcon,
  },
  props: {
    textButtonRemove: {
      type: String,
      required: true,
    },
    disabledButtonRemove: {
      type: Boolean,
      default: true,
    },
    batch: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSampleBeingRemoved: false,
      defaultOptionIndexText: 0,
      selectedTextOption: null,
      textQuery: '',
      selectOptions: [
        {
          id: 'patientName',
          label: 'Nome do Paciente',
          placeholder: 'Nome do Paciente',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginAlvaro',
          label: 'OS Alvaro',
          placeholder: 'Os Alvaro',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'codeOriginLis',
          label: 'OS Lis',
          placeholder: 'OS Lis',
          limitDays: null,
          singleDate: false,
        },
        {
          id: 'materialDescription',
          label: 'Material (Acond)',
          placeholder: 'Material (Acond)',
          limitDays: null,
          singleDate: false,
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['bathSelectedSamples', 'batchSamplesSorted']),
    samples() {
      return this.batchSamplesSorted.samples || []
    },
    iconRemoveURL() {
      return this.disabledButtonRemove
        ? '/static/images/icon-delete-default-disabled.svg'
        : '/static/images/icon-delete-default.svg'
    },
    isRemovingAllSamples() {
      return this.batch.total_samples === this.bathSelectedSamples.length
    },
    removeSamplesModalTitle() {
      if (this.isRemovingAllSamples) {
        return `Deseja remover todas (${this.bathSelectedSamples.length}) amostras do lote atual?`
      }

      return `Deseja remover (${this.bathSelectedSamples.length}) amostras do lote atual?`
    },
    removeSamplesModalDescription() {
      if (this.isRemovingAllSamples) {
        return `Ao continuar, as amostras selecionadas deste lote retornarão para a lista de amostras como 'pendente'.`
      }

      return `Ao continuar, todas amostras deste lote retornarão para a lista de amostras como “pendente”.`
    },
  },
  watch: {
    textQuery(newVal) {
      if (!newVal) {
        this.selectedTextOption = null
        this.setMultipleTextSearch({})
        this.handleFilterSamples()
      }
    },
  },
  methods: {
    ...mapActions(['fetchBatchSamples']),
    setMultipleTextSearch(e) {
      this.selectedTextOption = e
      this.textQuery = e?.query || ''

      this.patientNameRoot = ''
      this.codeOriginAlvaroRoot = ''
      this.codeOriginLisRoot = ''
      this.materialDescription = ''

      switch (e?.id) {
        case 'patientName':
          this.patientNameRoot = e?.query
          break
        case 'codeOriginAlvaro':
          this.codeOriginAlvaroRoot = e?.query
          break
        case 'codeOriginLis':
          this.codeOriginLisRoot = e?.query
          break
        case 'materialDescription':
          this.materialDescription = e?.query
          break
      }
    },
    async handleFilterSamples() {
      if (!this.selectOptions.length || !this.selectedTextOption) {
        return
      }

      await this.fetchBatchSamples()
    },
    async fetchBatchSamples() {
      await this.$store.dispatch('fetchBatchSamples', {
        batchId: this.$route.params.batchid,
        filters: {
          patientName: this.patientNameRoot,
          codeOriginAlvaro: this.codeOriginAlvaroRoot,
          codeOriginLis: this.codeOriginLisRoot,
          materialDescription: this.materialDescription,
        },
      })
    },
    handleOpenRemoveSamplesModal() {
      this.$refs.removeSamplesModal.open()
    },
    handleCloseRemoveSamplesModal() {
      this.$refs.removeSamplesModal.close()
    },
    async handleRemoveSelectedSamples() {
      this.isSampleBeingRemoved = true

      try {
        const samples = this.bathSelectedSamples
        const batchId = this.batch.id ?? this.batch.batchIdentifier
        const batchIdentifier = this.batch.batchIdentifier
        const isRemovingAllSamples =
          this.samples.length === this.bathSelectedSamples.length

        this.handleCloseRemoveSamplesModal()
        this.$refs.removeSampleFromBatchModal.open()

        for await (const sample of samples) {
          const sampleBarcode = sample.bar_code ?? sample.barcode

          if (this.batch.source === BATCH_SOURCE_SYSTEM_WS) {
            await batchService.removeSamplesFromWSBatch(sampleBarcode, batchIdentifier)
          } else {
            await batchService.removeSamplesFromBatch(sampleBarcode, batchId)
          }
        }

        notify({
          title: 'Amostra(s) removida(s) com sucesso.',
          style: 'success',
        })

        if (isRemovingAllSamples) {
          this.$router.replace('/create-batch')
        } else {
          this.$emit('samples-removed')
          this.updateBatchData()

          await this.fetchBatchSamples()
        }
      } catch (error) {
        console.error('Error removing selected samples.', error)

        notify({
          title: 'Erro:',
          message: 'Ocorreu um problema inesperado ao tentar remover as amostras.',
        })
      } finally {
        this.isSampleBeingRemoved = false
        this.handleCloseRemoveSamplesModal()
        this.$refs.removeSampleFromBatchModal.close()
      }
    },
    updateBatchData() {
      this.$emit('request-fetch-batch')
    },
  },
}
</script>

<style scoped lang="scss">
.batch-sample-filter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 20px;

  .search-field-selector {
    max-width: 399px;
    display: flex;
    align-items: center;

    .search {
      margin-top: 0;
    }

    ::v-deep(.search-field-selector) {
      height: 40px !important;
    }

    .btn-container {
      width: 48px;
      cursor: pointer;
      display: flex;
      height: 40px;
      margin-left: -4px;
      border-bottom-right-radius: 6px;
      border-top-right-radius: 6px;

      .md-icon {
        align-self: center;
        min-width: 16px;
        height: 16px;
        min-height: 16px;
        width: 16px;
      }
    }
  }
}

.remove-samples-modal {
  font-family: 'Exo 2';
  max-width: 486px;

  &-title {
    font-weight: 700;
    font-size: 1.125rem;
    color: #4a4a4a;
    margin-bottom: 0.5rem;
  }

  &-description {
    font-weight: 400;
    font-size: 1rem;
    color: #666666;
    margin-bottom: 1rem;
  }

  &-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
}

::v-deep(.button-icon button) {
  width: auto !important;
}

::v-deep(.button-icon button:disabled) {
  color: #d9d9d9 !important;
  border-color: #d9d9d9 !important;
  cursor: not-allowed;
}

::v-deep(.button-icon button:disabled .md-icon) {
  color: #d9d9d9 !important;
}
</style>
